// @ts-strict-ignore
import { OptinOptoutFormFields } from './Fields'
import { useNotifications } from '@components/NotificationsProvider'
import { Heading } from '@components/gassan-ui/Typography/Heading'
import { Button, Field, OldHeading, Icon, Select } from '@gassan-ui'
import { useUpdateNewsletterOptinMutation } from '@generated'
import { Form, Formik } from 'formik'
import { useTranslation } from 'next-i18next'
import { FC, useEffect, useRef, useState } from 'react'
import * as Yup from 'yup'
import { theme } from '@components/gassan-ui/theme'
import { cn } from '@lib/cn'

type OptinOptoutStatus = 'idle' | 'loading' | 'success'
type OptinOptoutFormProps = {
  email: string | null
}

export const OptinOptoutForm: FC<OptinOptoutFormProps> = ({ email }) => {
  const timer = useRef<NodeJS.Timeout>()
  const [status, setStatus] = useState<OptinOptoutStatus>('idle')
  const { t } = useTranslation(['account', 'notifications', 'other'])
  const notifications = useNotifications()
  const [, updateNewsletterOptin] = useUpdateNewsletterOptinMutation()

  useEffect(() => {
    if (status === 'success') {
      clearTimeout(timer.current)
      timer.current = setTimeout(() => {
        setStatus('idle')
      }, 5000)
    }

    return () => {
      clearTimeout(timer.current)
    }
  }, [status])

  let schema = Yup.object().shape({
    optin100: Yup.boolean(),
    optin101: Yup.boolean(),
    optin102: Yup.boolean(),
    reason: Yup.string().required(),
    customReason: Yup.string().when('reason', {
      is: 'other',
      then: (schema) => schema.required(),
    }),
  })

  const startValues = {
    optin100: false,
    optin101: false,
    optin102: false,
    reason: '',
    customReason: '',
  }

  if (!email) {
    return null
  }

  return (
    <Formik
      validationSchema={schema}
      initialValues={startValues}
      validateOnBlur
      validateOnChange
      onSubmit={async (values, { resetForm }) => {
        const { optin100, optin101, optin102, reason, customReason } = values
        const applicableReason = reason === 'other' ? customReason : reason

        const { data, error } = await updateNewsletterOptin({
          input: {
            email,
            optin100: !optin100,
            optin101: !optin101,
            optin102: !optin102,
            reason: applicableReason,
          },
        })
        if (error) {
          if (process.env.NODE_ENV === 'development') {
            console.error(error)
          }
          notifications.addNotification({
            variant: 'error',
            label: t('refreshAndTryAgain', { ns: 'notifications' }),
          })
        }

        if (data && data.updateNewsletterOptin) {
          setStatus('success')
          notifications.addNotification({
            variant: 'simple-confirmation',
            label: t('preferences.saved', { ns: 'account' }),
          })
          resetForm()
        }
      }}
    >
      {({ isSubmitting, setFieldValue, values, errors }) => (
        <Form>
          <OldHeading variant="h4" as="h2">
            {t('optOutTitle', { ns: 'other' })}
          </OldHeading>
          <div>
            <OptinOptoutFormFields />
            <div
              className={cn(
                'mb-6 mt-6 flex min-h-[176px] w-full flex-col items-start overflow-hidden bg-pampasLight p-4',
                'lg:p-8',
              )}
            >
              {status === 'success' ? (
                <div className="flex flex-1 items-center gap-4">
                  <Icon icon="account-check" color={theme.colors.tabaccoBrown} />
                  <Heading variant="h5" as="h3" className="mb-0">
                    {t('thanksForFeedback', { ns: 'other' })}{' '}
                    <a
                      target="_blank"
                      href="https://www.facebook.com/gassandiamonds"
                      rel="noreferrer"
                      className="font-semibold text-tabaccoBrown"
                    >
                      Facebook
                    </a>
                    ,{' '}
                    <a
                      target="_blank"
                      href="https://instagram.com/gassandiamonds"
                      rel="noreferrer"
                      className="font-semibold text-tabaccoBrown"
                    >
                      Instagram
                    </a>
                  </Heading>
                </div>
              ) : (
                <>
                  <Heading variant="h5" as="h3" className="mb-4">
                    {t('reasonUnsubscribing', { ns: 'other' })}
                  </Heading>
                  <Select
                    status={errors.reason ? 'error' : 'valid'}
                    value={values.reason}
                    onChange={(e) => setFieldValue('reason', e.target.value)}
                    className="w-full min-w-[250px]"
                  >
                    <option value=""></option>
                    {(Array.isArray(
                      t<string, any[]>('unsubscribeReasons', {
                        ns: 'other',
                        returnObjects: true,
                      }),
                    )
                      ? t<string, any[]>('unsubscribeReasons', {
                          ns: 'other',
                          returnObjects: true,
                        })
                      : []
                    ).map((reason, key) => {
                      const isLast =
                        key ===
                        (t<string, any[]>('unsubscribeReasons', {
                          ns: 'other',
                          returnObjects: true,
                        })?.length ?? 0) -
                          1
                      return (
                        <option value={isLast ? 'other' : reason} key={key}>
                          {reason}
                        </option>
                      )
                    })}
                  </Select>
                </>
              )}

              {values.reason === 'other' && (
                <Field
                  mt="2"
                  mb="4"
                  status={errors.customReason ? 'error' : 'valid'}
                  rows={2}
                  field="textarea"
                  name="customReason"
                  label={t('enterReason', { ns: 'other' })}
                  className="w-full min-w-[300px]"
                ></Field>
              )}
            </div>
            <Button variant="dark" type="submit" status={isSubmitting ? 'loading' : 'idle'}>
              {t('submitUnsubscribe', { ns: 'other' })}
            </Button>
          </div>
        </Form>
      )}
    </Formik>
  )
}
